import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { LoadState } from '../loadState';

export interface BrainConversationSummary {
  id: string;
  title: string | null;
  last_message_at: string;
  team_id: string | null;
}

interface BrainConversationsResult {
  items: BrainConversationSummary[];
  state: LoadState;
  errorMessage?: string;
  /** @deprecated alias kept while consumers migrate to `state`. */
  loading: boolean;
}

export function useBrainConversations(coachId: string | undefined, limit = 10): BrainConversationsResult {
  const [items, setItems] = useState<BrainConversationSummary[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!coachId) {
      setItems([]);
      setState('empty');
      setErrorMessage(undefined);
      return;
    }
    let cancelled = false;
    setState('loading');
    setErrorMessage(undefined);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('coach_conversations')
          .select('id, title, last_message_at, team_id')
          .eq('coach_id', coachId)
          .eq('archived', false)
          .order('last_message_at', { ascending: false })
          .limit(limit);
        if (cancelled) return;
        if (error) {
          setItems([]);
          setState('error');
          setErrorMessage(error.message);
          return;
        }
        const rows = (data ?? []) as BrainConversationSummary[];
        setItems(rows);
        setState(rows.length > 0 ? 'ok' : 'empty');
      } catch (err) {
        if (cancelled) return;
        console.warn('[brain conversations] threw', err);
        setItems([]);
        setState('error');
        setErrorMessage(err instanceof Error ? err.message : 'Could not load conversations.');
      }
    })();
    return () => { cancelled = true; };
  }, [coachId, limit]);

  return { items, state, errorMessage, loading: state === 'loading' };
}
